import {observer} from "mobx-react";
import {DefaultModalLayout} from "../../../../../components/modal/layouts/default-modal-layout";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import {FONT_COLOR, FONT_VARIANT} from "../../../../../components/fonts/font-props";
import {BUTTON_VARIANTS} from "../../../../../components/inputs/button-variants";
import {Button} from "../../../../../components/inputs/button/button";
import MediaWithPlaceholder, {
    MEDIA_VARIANTS
} from "../../../../../components/media-with-placeholder/media-with-placeholder";
import TextTruncate from "../../../../../components/text-truncate/text-truncate";
import Currency from "../../../../../components/currency/currency";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import {RadioButton} from "../../../../../components/inputs/radio-button/radio-button";
import {Textarea} from "../../../../../components/inputs/textarea/textarea";
import {Note, NOTE_TYPE} from "../../../../../components/note/note";
import './reject-items-modal.scss';
import {REJECT_TYPE} from "../../stores/rma-action-store/rma-reject-item-action-store";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import RmaPageStore, {IRmaRejectItemAction} from "../../stores/rma-page-store";
import ProductCaseStore from "../../stores/product-case-store";
import Hr from "../../../../../components/hr/hr";

interface IRejectItemsModalProps {
    rma: RmaPageStore;
    rmaAction: IRmaRejectItemAction;
    onCloseCallback: () => void;
}

const RejectItemPopup: React.FC<IRejectItemsModalProps> = observer(({rma, rmaAction, onCloseCallback}) => {
    const store = rmaAction.store;

    if (!rmaAction?.store || !store.getProductCaseForReject()) {
        return null;
    }

    const scope = 'dashboard-returns-page';
    const {getTranslation} = useDashboardTranslation();

    const selectedProductCaseToReject = store.getProductCaseForReject() as ProductCaseStore;

    const onSubmit = () => {
        store.submitAction(onCloseCallback);
    }

    const removeItemFromRejection = () => {
        store.removeProductCaseForReject();
        onCloseCallback();
    }

    return (
        <DefaultModalLayout
            isCloseDisabled={true}
            onCloseClicked={removeItemFromRejection}
        >
            <div className="reject-items-modal__container">

                <div className="reject-items-modal__header">
                    <FontBody1 variant={FONT_VARIANT.DEFAULT} color={FONT_COLOR.NEUTRAL3}>Item removal</FontBody1>
                    <FontBody1 color={FONT_COLOR.PRIMARY2}>
                        {getTranslation('REJECT_ITEM_MODAL_DO_YOU_WANT_TO_REMOVE_THIS_ITEM', scope)}
                    </FontBody1>
                </div>

                <div className="reject-items-modal__item-cards-wrapper">
                    <div className="reject-items-modal__item-card">
                        <MediaWithPlaceholder
                            variant={MEDIA_VARIANTS.XS}
                            alt={selectedProductCaseToReject.productStore.name}
                            enlargeSrc={selectedProductCaseToReject.productStore.imageUrl}
                            src={selectedProductCaseToReject.productStore.imageUrl}
                        />
                        <div className="reject-items-modal__item-info">
                            <TextTruncate>
                                <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                    {selectedProductCaseToReject.productStore.name}
                                </FontBody2>
                            </TextTruncate>
                            <TextTruncate>
                                <FontBody2 color={FONT_COLOR.NEUTRAL3}>
                                    {selectedProductCaseToReject.productStore.variantTitle}
                                </FontBody2>
                            </TextTruncate>
                            <TextTruncate>
                                <FontBody2 color={FONT_COLOR.NEUTRAL3}>
                                    {selectedProductCaseToReject.productStore.sku}
                                </FontBody2>
                            </TextTruncate>
                        </div>
                        <div className="reject-items-modal__item-price">
                            <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                <Currency
                                    currencyCode={selectedProductCaseToReject.itemsBalanceStore.originalItemPaidPrice?.currency_code}
                                    amount={+selectedProductCaseToReject.paidPrice}
                                />
                            </FontBody2>
                        </div>
                    </div>
                </div>

                <Hr/>

                <div className="reject-items-modal__removal-type">
                    <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                        {getTranslation('REJECT_ITEM_MODAL_SELECT_REMOVAL_TYPE', scope)}
                    </FontBody2>
                    <div className="reject-items-modal__radio-group">
                        <div className="reject-items-modal__radio">
                            <RadioButton
                                name={'remove-from-refund'}
                                checked={store.getRejectType() === REJECT_TYPE.REMOVE_FROM_REFUND}
                                onChange={() => store.setSelectedRejectType(REJECT_TYPE.REMOVE_FROM_REFUND)}
                            />
                            <div className="reject-items-modal__radio-texts">
                                <FontBody2 color={FONT_COLOR.PRIMARY2}>
                                    {getTranslation('REJECT_ITEM_MODAL_REMOVE_AMOUNT_FROM_REFUND', scope)}
                                </FontBody2>
                                <FontBody2 color={FONT_COLOR.PRIMARY3_50}>
                                    {getTranslation('REJECT_ITEM_MODAL_DONT_CALCULATE_THIS_ITEM_IN_REFUND', scope)}
                                </FontBody2>
                            </div>
                        </div>
                        <div className="reject-items-modal__radio">
                            <RadioButton
                                name="reject-item"
                                checked={store.getRejectType() === REJECT_TYPE.REJECT_ITEM}
                                onChange={() => store.setSelectedRejectType(REJECT_TYPE.REJECT_ITEM)}
                                disabled={!store.getProductCaseForReject()?.canBeRejected}
                            />
                            <div className="reject-items-modal__radio-texts">
                                <FontBody2 color={FONT_COLOR.PRIMARY2}>
                                    {getTranslation('REJECT_ITEM_MODAL_RADIO_BUTTON_REJECT_ITEM', scope)}
                                </FontBody2>
                                <FontBody2 color={FONT_COLOR.PRIMARY3_50}>
                                    {getTranslation('REJECT_ITEM_MODAL_RADIO_BUTTON_REJECT_ITEM_REFUND', scope)}
                                </FontBody2>
                            </div>
                        </div>
                    </div>
                </div>

                {!store.getProductCaseForReject()?.canBeRejected &&
                    <Note
                        type={NOTE_TYPE.ERROR}
                        body={getTranslation((store.getProductCaseForReject()?.canNotBeRejectedReason as string ?? ''), scope)}
                    />
                }

                <div className="reject-items-modal__reason">
                    {store.getRejectType() === REJECT_TYPE.REJECT_ITEM &&
                        <>
                            <FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
                                {getTranslation('REJECT_ITEM_MODAL_REJECT_REASON', scope)}
                            </FontBody2>
                            <Textarea
                                maxLength={100}
                                placeholder="Textarea (max 100 chars)"
                                value={store.getItemRejectReason() ?? undefined}
                                onChange={(v) => store.setItemRejectReason(v)}
                            />
                        </>
                    }
                </div>

                <Hr/>

                <div className="reject-items-modal__actions">
                    <Button variant={BUTTON_VARIANTS.Tertiary} onClick={removeItemFromRejection}
                            disabled={store.isProcessing}
                            text={getTranslation('REJECT_ITEM_MODAL_CANCEL_BUTTON_TEXT', scope)}
                    />
                    <Button variant={BUTTON_VARIANTS.Primary} onClick={onSubmit}
                            disabled={store.isProcessing}
                            text={getTranslation('REJECT_ITEM_MODAL_APPLY_CHANGE_BUTTON_TEXT', scope)}
                            isLoading={store.isProcessing}
                            hasLoader={true}
                    />
                </div>
            </div>
        </DefaultModalLayout>
    );
});

export default RejectItemPopup;